// Iter 127 — Help page.
// Sidebar (articles grouped by hub) + content panel. Searchable. Back-arrow
// returns the user to the page they were viewing before opening Help.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Search, BookOpen, Lightbulb, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { Input } from '../components/ui/input';
import { HELP_ARTICLES, helpArticlesByHub } from '../lib/helpArticles';

const HUB_LABEL = {
  dashboard: 'Dashboard',
  clinical: 'Clinical',
  knowledge: 'Knowledge',
  operations: 'Operations',
  compliance: 'Compliance',
};

const HUB_ORDER = ['dashboard', 'clinical', 'knowledge', 'operations', 'compliance'];

// Tiny inline markdown handler — supports **bold**, *italic*, and `code`.
// Avoiding a full markdown library keeps the bundle thin for what is a
// content-light page.
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let i = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) parts.push(<code key={i++} className="px-1 py-0.5 rounded bg-muted font-mono text-[12px]">{tok.slice(1, -1)}</code>);
    else parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function HelpPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialId = params.get('topic') || HELP_ARTICLES[0].id;
  const [activeId, setActiveId] = useState(initialId);
  const [q, setQ] = useState('');
  // Capture where the user came from so we can offer a true "Back" button.
  // If they typed the URL directly or refreshed, fall back to home.
  const returnTo = useRef(null);

  useEffect(() => {
    // history.state from react-router doesn't expose the previous path; the
    // simplest signal is document.referrer when it's same-origin.
    try {
      if (document.referrer && new URL(document.referrer).origin === window.location.origin) {
        returnTo.current = new URL(document.referrer).pathname + new URL(document.referrer).search;
      }
    } catch { /* ignore */ }
  }, []);

  // Keep URL ?topic= in sync so deep-links and refresh work
  useEffect(() => {
    if (activeId && params.get('topic') !== activeId) {
      setParams({ topic: activeId }, { replace: true });
    }
  }, [activeId, params, setParams]);

  const byHub = useMemo(() => helpArticlesByHub(), []);

  const filtered = useMemo(() => {
    if (!q.trim()) return HELP_ARTICLES;
    const ql = q.toLowerCase();
    return HELP_ARTICLES.filter(a =>
      a.title.toLowerCase().includes(ql) ||
      (a.summary || '').toLowerCase().includes(ql) ||
      (a.steps || []).some(s => s.toLowerCase().includes(ql))
    );
  }, [q]);

  const article = HELP_ARTICLES.find(a => a.id === activeId) || HELP_ARTICLES[0];

  const goBack = () => {
    if (returnTo.current && returnTo.current !== '/help') {
      nav(returnTo.current);
    } else if (window.history.length > 1) {
      nav(-1);
    } else {
      nav('/');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Sticky top-row: Back + title + search */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <button onClick={goBack} data-testid="help-back"
            className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-muted/40"
            title="Return to the previous page">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: '#0d2644' }} />
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: '#0d2644' }}>Help</h1>
          </div>
          <div className="ml-auto relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search help…"
                   data-testid="help-search" className="pl-8 text-sm" />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <aside className="space-y-4">
            {HUB_ORDER.map(hub => {
              const items = (byHub[hub] || []).filter(a => filtered.includes(a));
              if (items.length === 0) return null;
              return (
                <div key={hub}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 px-2">
                    {HUB_LABEL[hub]}
                  </div>
                  <div className="space-y-0.5">
                    {items.map(a => {
                      const active = a.id === activeId;
                      return (
                        <button key={a.id} onClick={() => setActiveId(a.id)}
                          data-testid={`help-topic-${a.id}`}
                          className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md transition-colors flex items-center justify-between gap-2
                            ${active ? 'bg-[#0d2644] text-white' : 'hover:bg-muted/50 text-foreground'}`}>
                          <span className="truncate">{a.title}</span>
                          {a.stub && (
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                              ${active ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                              soon
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {q.trim() && filtered.length === 0 && (
              <div className="text-xs text-muted-foreground italic px-2">
                No matches for &ldquo;{q}&rdquo;.
              </div>
            )}
          </aside>

          {/* Content panel */}
          <article data-testid={`help-article-${article.id}`}
                   className="rounded-xl border bg-card p-6 sm:p-8 prose-sm max-w-none">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">
              {HUB_LABEL[article.hub] || 'Help'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#0d2644' }}>
              {article.title}
            </h2>
            {article.summary && <p className="text-base text-muted-foreground mb-5">{article.summary}</p>}

            {article.stub ? (
              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Coming soon</p>
                <p>This guide is not written yet. Use the page directly — the UI is designed to be self-explanatory — and check back as we publish full walk-throughs.</p>
                {article.related && article.related.length > 0 && (
                  <p className="mt-2">See related: {article.related.map(r => {
                    const ra = HELP_ARTICLES.find(x => x.id === r);
                    return ra ? (
                      <button key={r} onClick={() => setActiveId(r)} className="underline text-foreground hover:text-[#0d2644] mr-2">
                        {ra.title}
                      </button>
                    ) : null;
                  })}</p>
                )}
              </div>
            ) : (
              <>
                {article.steps && article.steps.length > 0 && (
                  <ol className="space-y-3 mb-5 list-decimal pl-5 text-sm leading-relaxed marker:text-muted-foreground marker:font-bold">
                    {article.steps.map((s, i) => (
                      <li key={i} className="pl-1">{renderInline(s)}</li>
                    ))}
                  </ol>
                )}

                {article.tips && article.tips.length > 0 && (
                  <div className="rounded-lg border bg-amber-50/40 border-amber-200 p-3 mt-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1 inline-flex items-center gap-1.5 text-amber-700">
                      <Lightbulb className="w-3 h-3" /> Tips
                    </div>
                    <ul className="text-sm list-disc pl-5 space-y-1 marker:text-amber-500">
                      {article.tips.map((t, i) => <li key={i}>{renderInline(t)}</li>)}
                    </ul>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4 flex-wrap mt-6 pt-4 border-t text-xs text-muted-foreground">
                  <div>
                    {article.lastUpdated && <>Last updated <strong>{article.lastUpdated}</strong></>}
                  </div>
                  {article.related && article.related.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>Related:</span>
                      {article.related.map(r => {
                        const ra = HELP_ARTICLES.find(x => x.id === r);
                        return ra ? (
                          <button key={r} onClick={() => setActiveId(r)}
                                  className="inline-flex items-center gap-0.5 text-[#0d2644] font-semibold hover:underline">
                            {ra.title} <ArrowRight className="w-3 h-3" />
                          </button>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </article>
        </div>

        <p className="text-xs text-muted-foreground mt-6 text-center">
          Missing a topic? Ask the PM to <Link to="/permissions" className="underline">add a help article</Link> — or it might already be findable via <kbd className="font-mono bg-muted px-1 rounded">⌘K</kbd>.
        </p>
      </div>
    </div>
  );
}
